/**
 * Hook for managing payment instruments
 */
import { useCallback } from 'react';
import type { 
    CardPaymentMethod, 
    ApplePayToken, 
    GooglePayToken,
    BasisTheoryInstance,
    PaymentInstrumentResponse,
    PaymentInstrumentCustomerResponse 
} from '../../../core/resources/payments';
import type { PaymentsResource } from '../../../core/resources/payments';

interface UsePaymentInstrumentsParams {
    basisTheory: BasisTheoryInstance | undefined;
    paymentsResource: PaymentsResource;
    setError: (error: string | null) => void;
}

export function usePaymentInstruments({
    basisTheory,
    paymentsResource,
    setError,
}: UsePaymentInstrumentsParams) {
    // Create card payment instrument
    const createCardPaymentInstrument = useCallback(
        (cardData: CardPaymentMethod): Promise<PaymentInstrumentResponse> => {
            return paymentsResource.createCardPaymentInstrument(basisTheory, cardData);
        },
        [basisTheory, paymentsResource],
    );

    // Create Apple Pay payment instrument
    const createApplePayPaymentInstrument = useCallback(
        (applePayToken: ApplePayToken): Promise<PaymentInstrumentResponse> => {
            return paymentsResource.createApplePayPaymentInstrument(basisTheory, applePayToken);
        },
        [basisTheory, paymentsResource],
    );

    // Create Google Pay payment instrument
    const createGooglePayPaymentInstrument = useCallback(
        (googlePayToken: GooglePayToken): Promise<PaymentInstrumentResponse> => {
            return paymentsResource.createGooglePayPaymentInstrument(basisTheory, googlePayToken);
        },
        [basisTheory, paymentsResource],
    );

    // Get card payment instruments
    const getCardPaymentInstruments = useCallback(async (): Promise<PaymentInstrumentCustomerResponse> => {
        try {
            const response = await paymentsResource.getCardPaymentInstruments();
            return response;
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Failed to fetch payment instruments';
            setError(errorMsg);
            throw error;
        }
    }, [paymentsResource, setError]);

    return {
        createCardPaymentInstrument,
        createApplePayPaymentInstrument,
        createGooglePayPaymentInstrument,
        getCardPaymentInstruments,
    };
}
